import { type CalendarDto, type CalendarItemDto } from '../../services/api';
import { icon } from '../../icons';
import { i18nService, t, translateDocumentType, translateEventType } from '../../services/i18n';
import { buildPropertyDetailsUrl } from '../property/property-navigation';

export type CalendarViewMode = 'day' | 'week';
export type CalendarSourceFilter = 'all' | CalendarItemDto['sourceType'];

export function esc(str: string): string {
  const el = document.createElement('span');
  el.textContent = str;
  return el.innerHTML;
}

export function createLocalDate(input: Date): Date {
  return new Date(input.getFullYear(), input.getMonth(), input.getDate(), 12, 0, 0, 0);
}

export function dateFromIso(isoDate: string): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function toIsoDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return createLocalDate(next);
}

export function startOfWeek(date: Date): Date {
  const normalized = createLocalDate(date);
  const dayOfWeek = (normalized.getDay() + 6) % 7;
  normalized.setDate(normalized.getDate() - dayOfWeek);
  return normalized;
}

export function formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  return i18nService.formatDate(dateStr, options ?? { weekday: 'short', day: 'numeric', month: 'short' });
}

export function formatRangeLabel(data: CalendarDto | null): string {
  if (!data) return '';
  if (data.startDate === data.endDate) {
    return formatDate(data.startDate, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  }
  const start = dateFromIso(data.startDate);
  const end = dateFromIso(data.endDate);
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${i18nService.formatDate(data.startDate, { day: 'numeric' })} - ${i18nService.formatDate(data.endDate, { day: 'numeric', month: 'long', year: 'numeric' })}`;
  }
  return `${i18nService.formatDate(data.startDate, { day: 'numeric', month: 'short' })} - ${i18nService.formatDate(data.endDate, { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

export function translateSource(item: CalendarItemDto): string {
  switch (item.sourceType) {
    case 'Reminder': return t('calendar.source.reminder');
    case 'DocumentExpiry': return t('calendar.source.documentExpiry');
    case 'AgreementEnd': return t('calendar.source.agreementEnd');
    case 'TimelineEvent': return t('calendar.source.timelineEvent');
  }
}

export function itemIcon(item: CalendarItemDto): string {
  switch (item.sourceType) {
    case 'Reminder': return icon('bell', { size: 14 });
    case 'DocumentExpiry': return icon('file-text', { size: 14 });
    case 'AgreementEnd': return icon('key', { size: 14 });
    case 'TimelineEvent': return icon('calendar', { size: 14 });
  }
}

export function itemTitle(item: CalendarItemDto): string {
  return item.sourceType === 'AgreementEnd' ? t('calendar.item.agreementEndTitle') : item.title;
}

export function itemMeta(item: CalendarItemDto): string {
  const parts: string[] = [];
  if (item.sourceType === 'DocumentExpiry' && item.detailType) {
    parts.push(translateDocumentType(item.detailType));
  } else if (item.sourceType === 'TimelineEvent' && item.detailType) {
    parts.push(translateEventType(item.detailType));
  } else if (item.sourceType === 'Reminder' && item.detailType === 'Recurring') {
    parts.push(t('calendar.item.recurring'));
  }
  if (item.propertyAddress) parts.push(`${t('common.property')}: ${item.propertyAddress}`);
  if (item.tenantName) parts.push(`${t('common.tenant')}: ${item.tenantName}`);
  return parts.map((part) => esc(part)).join(' · ');
}

export function buildPropertyLink(item: CalendarItemDto): string | null {
  if (!item.propertyId) return null;
  return buildPropertyDetailsUrl(item.propertyId);
}

export function buildTenantLink(item: CalendarItemDto): string | null {
  if (!item.tenantId) return null;
  const params = new URLSearchParams({ tenant: item.tenantId });
  if (item.propertyId) {
    params.set('property', item.propertyId);
  }
  return `/tenants/?${params.toString()}`;
}

export function getUrgencyLabel(item: CalendarItemDto): string | null {
  const today = createLocalDate(new Date());
  const itemDate = dateFromIso(item.date);
  const diffDays = Math.round((itemDate.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return t('common.overdue');
  if (diffDays === 0) return t('common.today');
  if (diffDays === 1) return t('common.tomorrow');
  if (diffDays <= 7) return t('common.soon');
  return null;
}

export function getUrgencyClass(item: CalendarItemDto): string {
  const today = createLocalDate(new Date());
  const itemDate = dateFromIso(item.date);
  const diffDays = Math.round((itemDate.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) return 'overdue';
  if (diffDays <= 1) return 'today';
  if (diffDays <= 7) return 'soon';
  return 'upcoming';
}
